/* eslint-disable react/display-name */
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";

import {
    Icon,
    Accordion,
    AccordionDetails,
    AccordionSummary
} from "@mui/material";

import _ from "lodash";

const ResultsAmountLabel = styled.span`
    color: rgba(34, 34, 34, 0.25);
    text-align: right;
    font-family: "Source Sans Pro";
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 16.5px */
    text-transform: uppercase;
`;

const StyledSlideNumberTitle = styled.span`
    color: #222;
    font-family: "Source Sans Pro";
    font-size: 11px;
    font-style: normal;
    font-weight: 600;
    line-height: 14px; /* 127.273% */
    text-transform: uppercase;
`;

const StyledAccordion = styled(Accordion)`
    && {
        box-shadow: none;
        width: 100%;
        min-height: 0;
        position: relative;

        &::after {
            content: "";
            position: absolute;
            bottom: 0;
            right: 0;
            width: calc(100% - 10px);
            height: 1px;
            background-color: #F9F9F9;
        }

        &.MuiAccordion-root:before {
            background-color: transparent;
        }

        &.Mui-expanded {
            margin: 0;
        }

        &.MuiAccordion-rounded {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }
    }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
    &&& {

        &.MuiAccordionSummary-root{
            padding: 9px 15px 9px 0;
            min-height: 0;
        }

        &&.MuiAccordionSummary-root .MuiAccordionSummary-content{
            margin: 0;
        }        
    }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
    &&& {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 10px;
        padding: 0;

    }
`;

const AccordionContent = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

const MatchEntry = styled.div`
     cursor: pointer;
    background-color: ${({ selected }) => selected ? "#DAE8EE" : "transparent"};
    width: 100%;
    padding: 9px 24px;
`;

const ResultsContainer = styled.div`
    position: relative;
    overflow: auto;
    flex: 1;
    width: 100%;
    
    ::-webkit-scrollbar {
        width: 5px;
    }

    ::-webkit-scrollbar-thumb {
        background: #11a9e2;
    }
`;

const SearchResults = React.forwardRef(({ findTextResults, selectedIndex, onClick }, ref) => {
    const [isTop, setIsTop] = useState(true);
    const [isBottom, setIsBottom] = useState(false);
    const selectedItemRef = useRef(null);

    const [expandedAccordions, setExpandedAccordions] = useState(() => {
        const initialState = {};
        findTextResults.forEach(result => {
            initialState[result.index] = true; // Default to expanded
        });
        return initialState;
    });

    const toggleAccordion = slideIdx => {
        setExpandedAccordions(prevState => ({
            ...prevState,
            [slideIdx]: !prevState[slideIdx]
        }));
    };

    useEffect(() => {
        if (!selectedItemRef.current) return;

        selectedItemRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest"
        });
    }, [selectedIndex]);

    useEffect(() => {
        const handleScroll = () => {
            if (!ref) return;
            if (!ref.current) return;
            const { scrollTop, scrollHeight, clientHeight } = ref.current;
            setIsTop(scrollTop === 0);
            setIsBottom(scrollTop + clientHeight === scrollHeight);
        };

        const container = ref.current;
        container.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, []);

    if (findTextResults.length === 0) return null;

    const groupedResults = findTextResults.reduce((acc, item) => {
        acc[item.index] = acc[item.index] || [];
        acc[item.index].push(item);
        return acc;
    }, {});

    let runningIndex = 0;

    return (
        <ResultsContainer ref={ref} >
            {Object.entries(groupedResults).map(([slideIdx, group]) => {
                return (
                    <StyledAccordion
                        key={slideIdx}
                        expanded={!!expandedAccordions[slideIdx]}
                        onChange={() => toggleAccordion(slideIdx)}
                    >
                        <StyledAccordionSummary expandIcon={<Icon>arrow_drop_down</Icon>}
                            IconButtonProps={{
                                style: {
                                    order: -1,
                                    padding: 0,
                                    margin: 0,
                                }
                            }}>
                            <AccordionContent>
                                <StyledSlideNumberTitle>Slide {Number(slideIdx) + 1}</StyledSlideNumberTitle>
                                <ResultsAmountLabel>{group.length}</ResultsAmountLabel>
                            </AccordionContent>
                        </StyledAccordionSummary>
                        <StyledAccordionDetails>
                            {group.map(r => {
                                const index = runningIndex++;
                                const isSelected = index === selectedIndex;

                                return (
                                    <MatchEntry
                                        key={index}
                                        selected={isSelected}
                                        onClick={() => onClick(index, r)}
                                        ref={isSelected ? selectedItemRef : null}
                                    >
                                        {r.before}
                                        <strong>{r.match}</strong>
                                        {r.after}
                                    </MatchEntry>
                                );
                            })}
                        </StyledAccordionDetails>
                    </StyledAccordion>
                );
            })}
        </ResultsContainer>
    );
});

export default SearchResults;
